import React, { memo } from 'react'
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { Add, Close, Remove } from '@mui/icons-material'
import { TFunction } from 'i18next'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { LocationItem, LocationsPanelMode, LocationsSort, LocationsSortKey } from './types'
import { MAX_RADIUS, MIN_RADIUS } from './radius'

const ROW_HEIGHT = 36

const cellSx = { py: 0, height: ROW_HEIGHT, borderColor: 'divider' } as const
const headCellSx = {
  ...cellSx,
  bgcolor: 'action.hover',
  fontWeight: 500,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
} as const

// Not padding="checkbox": in a small table its width (24px) beats sx, and the theme's 2rem cell icons overflow it
const checkboxCellSx = { width: 48, px: 0, textAlign: 'center', verticalAlign: 'middle' } as const
const checkboxSx = { p: 0.5 } as const

type LocationRowProps = {
  item: LocationItem
  isEdit: boolean
  showArea: boolean
  showRadius: boolean
  checked: boolean
  highlighted: boolean
  onToggle: (index: number) => void
  onStepRadius: (index: number, direction: 1 | -1) => void
  onRemove: (index: number) => void
  t: TFunction
}

// Memoized: a radius change or a selection only re-renders the rows it touches
const LocationRow = memo(function LocationRow({
  item,
  isEdit,
  showArea,
  showRadius,
  checked,
  highlighted,
  onToggle,
  onStepRadius,
  onRemove,
  t,
}: LocationRowProps) {
  const radius = item.radius ?? MIN_RADIUS
  const background = highlighted ? 'warning.light' : checked ? 'action.selected' : undefined

  return (
    <TableRow
      hover
      selected={checked}
      data-highlighted={highlighted || undefined}
      sx={{ '& td': { bgcolor: background } }}
    >
      {isEdit && (
        <TableCell sx={{ ...cellSx, ...checkboxCellSx }}>
          <Checkbox
            size="small"
            sx={checkboxSx}
            checked={checked}
            onChange={() => onToggle(item.index)}
            slotProps={{ input: { 'aria-label': t('adgroup.geoTargeting.panel.selectLocation', { name: item.name }) } }}
          />
        </TableCell>
      )}
      <TableCell
        sx={{
          ...cellSx,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: highlighted ? 500 : 400,
        }}
      >
        {item.name}
      </TableCell>
      {showArea && (
        <TableCell
          sx={{
            ...cellSx,
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.area?.label ?? ''}
        </TableCell>
      )}
      {showRadius && isEdit && (
        <TableCell sx={cellSx}>
          <Box
            sx={{ display: 'inline-flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <IconButton
              size="small"
              sx={{ p: 0.25, borderRadius: 1 }}
              disabled={radius <= MIN_RADIUS}
              onClick={() => onStepRadius(item.index, -1)}
              aria-label={t('adgroup.geoTargeting.panel.decreaseRadius', { name: item.name })}
            >
              <Remove sx={{ fontSize: 14 }} />
            </IconButton>
            <Typography
              component="span"
              sx={{
                minWidth: 44,
                textAlign: 'center',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {t('adgroup.geoTargeting.panel.radiusValue', { radius })}
            </Typography>
            <IconButton
              size="small"
              sx={{ p: 0.25, borderRadius: 1 }}
              disabled={radius >= MAX_RADIUS}
              onClick={() => onStepRadius(item.index, 1)}
              aria-label={t('adgroup.geoTargeting.panel.increaseRadius', { name: item.name })}
            >
              <Add sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </TableCell>
      )}
      {showRadius && !isEdit && (
        <TableCell align="right" sx={{ ...cellSx, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
          {t('adgroup.geoTargeting.panel.radiusValue', { radius })}
        </TableCell>
      )}
      {isEdit && (
        <TableCell align="right" sx={{ ...cellSx, pl: 0, pr: 1 }}>
          <IconButton
            size="small"
            sx={{ p: 0.5 }}
            onClick={() => onRemove(item.index)}
            aria-label={t('adgroup.geoTargeting.panel.removeLocation', { name: item.name })}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  )
})

type LocationsTableProps = {
  items: LocationItem[]
  mode: LocationsPanelMode
  /** Translation key of the name column */
  nameColumn: string
  /** Translation key of the area column, undefined to hide it */
  areaColumn?: string
  showRadius: boolean
  sort: LocationsSort
  onSort: (key: LocationsSortKey) => void
  selected: ReadonlySet<number>
  highlightedIndex?: number
  onToggle: (index: number) => void
  onTogglePage: () => void
  onStepRadius: (index: number, direction: 1 | -1) => void
  onRemove: (index: number) => void
  label: string
}

const LocationsTable: React.FC<LocationsTableProps> = ({
  items,
  mode,
  nameColumn,
  areaColumn,
  showRadius,
  sort,
  onSort,
  selected,
  highlightedIndex,
  onToggle,
  onTogglePage,
  onStepRadius,
  onRemove,
  label,
}) => {
  const t = useAppTranslations()
  const isEdit = mode === 'edit'
  const selectedOnPage = items.filter((item) => selected.has(item.index)).length

  const sortHeader = (key: LocationsSortKey, column: string): React.ReactNode => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.dir : 'asc'}
      onClick={() => onSort(key)}
      sx={{ fontSize: 'inherit' }}
    >
      {t(`adgroup.geoTargeting.panel.${column}`)}
    </TableSortLabel>
  )
  const ariaSort = (key: LocationsSortKey): 'ascending' | 'descending' | undefined =>
    sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined

  return (
    <Table size="small" aria-label={label} sx={{ tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow>
          {isEdit && (
            <TableCell sx={{ ...headCellSx, ...checkboxCellSx }}>
              <Checkbox
                size="small"
                sx={checkboxSx}
                checked={items.length > 0 && selectedOnPage === items.length}
                indeterminate={selectedOnPage > 0 && selectedOnPage < items.length}
                disabled={items.length === 0}
                onChange={onTogglePage}
                slotProps={{ input: { 'aria-label': t('adgroup.geoTargeting.panel.selectPage') } }}
              />
            </TableCell>
          )}
          <TableCell sx={headCellSx} aria-sort={ariaSort('name')}>
            {sortHeader('name', nameColumn)}
          </TableCell>
          {areaColumn && (
            <TableCell sx={{ ...headCellSx, width: '38%' }} aria-sort={ariaSort('area')}>
              {sortHeader('area', areaColumn)}
            </TableCell>
          )}
          {showRadius && (
            <TableCell
              sx={{ ...headCellSx, width: isEdit ? 116 : 76 }}
              align={isEdit ? 'left' : 'right'}
              aria-sort={ariaSort('radius')}
            >
              {sortHeader('radius', 'columnRadius')}
            </TableCell>
          )}
          {isEdit && (
            <TableCell sx={{ ...headCellSx, width: 40 }}>
              <Box
                component="span"
                sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
              >
                {t('adgroup.geoTargeting.panel.removeSelected')}
              </Box>
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => (
          <LocationRow
            key={item.index}
            item={item}
            isEdit={isEdit}
            showArea={Boolean(areaColumn)}
            showRadius={showRadius}
            checked={isEdit && selected.has(item.index)}
            highlighted={item.index === highlightedIndex}
            onToggle={onToggle}
            onStepRadius={onStepRadius}
            onRemove={onRemove}
            t={t}
          />
        ))}
      </TableBody>
    </Table>
  )
}

export default LocationsTable
